/**
 * 班主任请假登记(博颂 2026-10-08):班主任 / 校长 / 主任给本班学生登记某天或一段日期的请假;
 * 这些天里所有点名册(行政班、分层课、每一节)这个学生默认「请假」并显示登记人,
 * 任课老师不用再去问。已经被记成「缺勤」的节次会改成请假并撤掉缺勤扣分。
 */
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { useLeaveMutations, useLeaves, type LeaveKind } from "../../api/lessons";

const KINDS: LeaveKind[] = ["personal", "sick", "other"];

export function LeavePanel({ homeroomId, students, canEdit, onClose }: { homeroomId: string; students: { id: string; name_zh: string; name_en?: string | null }[]; canEdit: boolean; onClose: () => void }) {
  const { t } = useTranslation();
  const leaves = useLeaves(homeroomId);
  const m = useLeaveMutations(homeroomId);
  const today = new Date().toISOString().slice(0, 10);
  const [studentId, setStudentId] = useState("");
  const [start, setStart] = useState(today);
  const [end, setEnd] = useState(today);
  const [kind, setKind] = useState<LeaveKind>("personal");
  const [note, setNote] = useState("");
  const [msg, setMsg] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  async function add() {
    setErr(null); setMsg(null);
    try {
      const out = await m.add.mutateAsync({ student_id: studentId, start_date: start, end_date: end < start ? start : end, kind, note: note.trim() || null });
      setMsg(t("lessons.leave.added", { n: out.converted_absences }));
      setStudentId(""); setNote("");
    } catch (e) { setErr((e as Error).message); }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/30 p-0 sm:items-center sm:p-4" onClick={onClose}>
      <div className="flex max-h-[90vh] w-full max-w-2xl flex-col rounded-t-xl bg-white shadow-xl sm:rounded-xl" onClick={(e) => e.stopPropagation()} data-testid="leave-panel">
        <div className="flex items-start justify-between border-b border-slate-200 px-4 py-3">
          <div>
            <div className="text-sm font-semibold text-slate-800">{t("lessons.leave.title")}</div>
            <p className="text-xs text-slate-500">{t("lessons.leave.hint")}</p>
          </div>
          <button type="button" onClick={onClose} className="text-slate-400 hover:text-slate-600" aria-label={t("common.close")}>×</button>
        </div>
        {canEdit && (
          <div className="grid gap-2 border-b border-slate-100 px-4 py-3 text-xs sm:grid-cols-6">
            <label className="sm:col-span-2">{t("lessons.leave.student")}
              <select id="leave-student" value={studentId} onChange={(e) => setStudentId(e.target.value)} className="mt-1 w-full rounded border border-slate-200 px-2 py-1">
                <option value="">{t("lessons.leave.pickStudent")}</option>
                {students.map((s) => <option key={s.id} value={s.id}>{s.name_zh}{s.name_en ? ` ${s.name_en}` : ""}</option>)}
              </select>
            </label>
            <label>{t("lessons.leave.start")}<input id="leave-start" type="date" value={start} onChange={(e) => { setStart(e.target.value); if (end < e.target.value) setEnd(e.target.value); }} className="mt-1 w-full rounded border border-slate-200 px-2 py-1" /></label>
            <label>{t("lessons.leave.end")}<input id="leave-end" type="date" value={end} min={start} onChange={(e) => setEnd(e.target.value)} className="mt-1 w-full rounded border border-slate-200 px-2 py-1" /></label>
            <label>{t("lessons.leave.kind")}
              <select id="leave-kind" value={kind} onChange={(e) => setKind(e.target.value as LeaveKind)} className="mt-1 w-full rounded border border-slate-200 px-2 py-1">
                {KINDS.map((k) => <option key={k} value={k}>{t(`lessons.leave.kinds.${k}`)}</option>)}
              </select>
            </label>
            <label>{t("lessons.note")}<input id="leave-note" value={note} onChange={(e) => setNote(e.target.value)} maxLength={200} className="mt-1 w-full rounded border border-slate-200 px-2 py-1" placeholder={t("lessons.leave.notePh")} /></label>
            <div className="flex items-center gap-2 sm:col-span-6">
              {msg && <span className="text-emerald-700">{msg}</span>}
              {err && <span className="text-rose-600">{err}</span>}
              <button type="button" disabled={!studentId || m.add.isPending} onClick={() => void add()} className="ml-auto rounded bg-sky-500 px-3 py-1 font-medium text-white hover:bg-sky-600 disabled:opacity-50" data-testid="leave-add">
                {t("lessons.leave.add")}
              </button>
            </div>
          </div>
        )}
        <div className="min-h-0 flex-1 overflow-y-auto px-4 py-2">
          {(leaves.data ?? []).length === 0 && <p className="py-4 text-center text-xs text-slate-400">{t("lessons.leave.none")}</p>}
          <ul className="divide-y divide-slate-100 text-sm" data-testid="leave-list">
            {(leaves.data ?? []).map((l) => (
              <li key={l.id} className="flex flex-wrap items-baseline gap-x-3 gap-y-0.5 py-2">
                <span className="font-medium text-slate-800">{l.name_zh}</span>
                <span className="rounded bg-sky-50 px-1.5 py-0.5 text-xs text-sky-700">{t(`lessons.leave.kinds.${l.kind}`)}</span>
                <span className="text-xs tabular-nums text-slate-600">{l.start_date === l.end_date ? l.start_date : `${l.start_date} ~ ${l.end_date}`}</span>
                {l.note && <span className="text-xs text-slate-500">{l.note}</span>}
                <span className="ml-auto text-[11px] text-slate-400">{l.by ?? "—"}</span>
                {canEdit && (
                  <button type="button" className="text-xs text-rose-500 hover:underline" onClick={() => { if (window.confirm(t("lessons.leave.removeConfirm", { name: l.name_zh }))) m.remove.mutate(l.id); }}>
                    {t("lessons.leave.remove")}
                  </button>
                )}
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}
